import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import MainLayout from "../components/mainLayout";
import Card from "../components/ui/card";
import MapaClientesSP from "../components/MapaClientesSP";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../sass/pages/dashboard.scss";
import {
  Users,
  ClipboardList,
  CalendarCheck,
  Activity,
  Layers,
  AlertTriangle,
  CheckCircle,
  Clock,
  ListChecks,
  MapPinned,
  SlidersHorizontal,
  RefreshCcw,
} from "lucide-react";
import { showError } from "../utils/toast";

const DASH_SECTION_IDS = [
  "indicadores-principais",
  "indicadores-secundarios",
  "mapa-clientes-sp",
  "leads-metricas",
  "chamados-mensal-periodo",
  "chamados-tecnico-urgencia",
  "clientes-problemas",
  "backlog-subproblemas",
  "delivery-kpis",
  "projetos-status-prioridade",
  "projetos-atrasados-dev",
];

const SECTION_TITLES = {
  "indicadores-principais": "Indicadores principais",
  "indicadores-secundarios": "Indicadores operacionais",
  "mapa-clientes-sp": "Mapa de clientes ativos",
  "leads-metricas": "Leads e oportunidades",
  "chamados-mensal-periodo": "Chamados no período",
  "chamados-tecnico-urgencia": "Técnicos e urgência",
  "clientes-problemas": "Clientes e problemas recorrentes",
  "backlog-subproblemas": "Backlog e subproblemas",
  "delivery-kpis": "Delivery de projetos",
  "projetos-status-prioridade": "Status e prioridade",
  "projetos-atrasados-dev": "Atrasos e carga por usuário",
};

const CHART_COLORS = [
  "#38bdf8",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#14b8a6",
  "#60a5fa",
  "#f97316",
];

const URGENCIA_LABEL = {
  0: "0 - Baixa",
  1: "1 - Média",
  2: "2 - Alta",
  3: "3 - Crítica",
};

const getUserLayoutKey = () => {
  if (typeof window === "undefined") return "SSW_DASH_LAYOUT_ANON";

  const idFunc =
    window.sessionStorage.getItem("IdFuncionario") ||
    window.localStorage.getItem("IdFuncionario");

  const codFunc =
    window.sessionStorage.getItem("codFunc") ||
    window.localStorage.getItem("codFunc");

  return `SSW_DASH_LAYOUT_${idFunc || codFunc || "anon"}`;
};

const pick = (obj, keys, fallback = undefined) => {
  if (!obj) return fallback;

  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }

  return fallback;
};

const numberOrZero = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const textOrDefault = (value, fallback = "Não informado") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(
    numberOrZero(value)
  );

const formatDatePt = (value) => {
  if (!value) return null;

  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);

  if (!match) return text;

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

const formatDateTimePt = (value) => {
  if (!value) return "não informado";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "não informado";

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const asArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }

  return [];
};

const sumBy = (list, key) =>
  asArray(list).reduce((total, item) => total + numberOrZero(item?.[key]), 0);

const hasChartData = (list, key = "total") => sumBy(list, key) > 0;

const getPeriodoParams = (filtros) => {
  const params = {};
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;

  if (filtros.periodo === "MES_ATUAL") {
    params.ano = anoAtual;
    params.mes = mesAtual;
  }

  if (filtros.periodo === "ANO_ATUAL") {
    params.ano = anoAtual;
  }

  if (filtros.periodo === "INTERVALO") {
    if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros.dataFim) params.dataFim = filtros.dataFim;
  }

  return params;
};

const EmptyChart = ({ title = "Sem dados para exibir", description }) => (
  <div className="grafico-empty-state">
    <div className="grafico-empty-icon">
      <Activity size={18} />
    </div>
    <strong>{title}</strong>
    {description && <span>{description}</span>}
  </div>
);

const SectionShell = ({ title, subtitle, children, right }) => (
  <section className="dashboard-section">
    {(title || subtitle || right) && (
      <div className="section-header">
        <div>
          {title && <h2 className="section-title">{title}</h2>}
          {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>
        {right && <div className="section-header-actions">{right}</div>}
      </div>
    )}
    {children}
  </section>
);

const ChartCard = ({
  title,
  subtitle,
  children,
  empty,
  emptyTitle,
  emptyDescription,
  className = "",
  meta,
}) => (
  <div className={`grafico ${className}`}>
    <div className="grafico-header">
      <div>
        <h3 className="grafico-titulo">{title}</h3>
        {subtitle && <span className="grafico-sub">{subtitle}</span>}
      </div>
      {meta && <div className="grafico-meta">{meta}</div>}
    </div>

    {empty ? (
      <EmptyChart title={emptyTitle} description={emptyDescription} />
    ) : (
      <div className="grafico-body">{children}</div>
    )}
  </div>
);

const BarVisual = ({
  data = [],
  labelKey = "label",
  valueKey = "total",
  mode = "vertical",
  maxItems = 12,
}) => {
  const items = asArray(data).slice(0, maxItems);
  const max = Math.max(...items.map((item) => numberOrZero(item[valueKey])), 1);

  if (mode === "horizontal") {
    return (
      <div className="dash-bar-list">
        {items.map((item, idx) => {
          const value = numberOrZero(item[valueKey]);
          const width = Math.max(value > 0 ? 8 : 0, Math.round((value / max) * 100));

          return (
            <div className="dash-bar-row" key={`${item[labelKey]}-${idx}`}>
              <div className="dash-bar-row-label" title={item[labelKey]}>
                {item[labelKey]}
              </div>
              <div className="dash-bar-row-track">
                <span
                  className="dash-bar-row-fill"
                  style={{
                    width: `${width}%`,
                    background: CHART_COLORS[idx % CHART_COLORS.length],
                  }}
                />
              </div>
              <strong>{formatNumber(value)}</strong>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="dash-column-chart">
      {items.map((item, idx) => {
        const value = numberOrZero(item[valueKey]);
        const height = Math.max(value > 0 ? 8 : 0, Math.round((value / max) * 100));

        return (
          <div className="dash-column-item" key={`${item[labelKey]}-${idx}`}>
            <span className="dash-column-value">{value > 0 ? formatNumber(value) : ""}</span>
            <div className="dash-column-track">
              <span
                className="dash-column-fill"
                style={{
                  height: `${height}%`,
                  background: `linear-gradient(180deg, ${
                    CHART_COLORS[idx % CHART_COLORS.length]
                  }, rgba(37, 99, 235, 0.78))`,
                }}
              />
            </div>
            <small title={item[labelKey]}>{item[labelKey]}</small>
          </div>
        );
      })}
    </div>
  );
};

const DonutVisual = ({ data = [], labelKey = "label", valueKey = "total", maxItems = 8 }) => {
  const items = asArray(data)
    .filter((item) => numberOrZero(item[valueKey]) > 0)
    .slice(0, maxItems);

  const total = items.reduce((acc, item) => acc + numberOrZero(item[valueKey]), 0);

  let acc = 0;
  const gradient =
    total > 0
      ? items
          .map((item, idx) => {
            const value = numberOrZero(item[valueKey]);
            const start = (acc / total) * 100;
            acc += value;
            const end = (acc / total) * 100;
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            return `${color} ${start}% ${end}%`;
          })
          .join(", ")
      : "rgba(148, 163, 184, 0.25) 0% 100%";

  return (
    <div className="dash-donut-chart">
      <div
        className="dash-donut"
        style={{
          background: `conic-gradient(${gradient})`,
        }}
      >
        <div>
          <strong>{formatNumber(total)}</strong>
          <span>Total</span>
        </div>
      </div>

      <div className="dash-donut-legend">
        {items.map((item, idx) => (
          <div className="dash-donut-legend-row" key={`${item[labelKey]}-${idx}`}>
            <i style={{ background: CHART_COLORS[idx % CHART_COLORS.length] }} />
            <span title={item[labelKey]}>{item[labelKey]}</span>
            <strong>{formatNumber(item[valueKey])}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrendVisual = ({ data = [] }) => {
  const items = asArray(data);
  const width = 620;
  const height = 230;
  const padX = 34;
  const padY = 28;

  const max = Math.max(
    ...items.map((item) => Math.max(numberOrZero(item.resolvidos), numberOrZero(item.pendentes))),
    1
  );

  const getPoint = (item, idx, key) => {
    const x =
      items.length === 1
        ? width / 2
        : padX + (idx * (width - padX * 2)) / Math.max(items.length - 1, 1);
    const y =
      height -
      padY -
      (numberOrZero(item[key]) / max) * (height - padY * 2);

    return { x, y };
  };

  const makePath = (key) =>
    items
      .map((item, idx) => {
        const point = getPoint(item, idx, key);
        return `${idx === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
      })
      .join(" ");

  return (
    <div className="dash-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} />
        <line x1={padX} y1={padY} x2={padX} y2={height - padY} />

        {[0.25, 0.5, 0.75].map((p) => {
          const y = padY + p * (height - padY * 2);
          return <line key={p} className="grid" x1={padX} y1={y} x2={width - padX} y2={y} />;
        })}

        <path className="line-resolvidos" d={makePath("resolvidos")} />
        <path className="line-pendentes" d={makePath("pendentes")} />

        {items.map((item, idx) => {
          const r = getPoint(item, idx, "resolvidos");
          const p = getPoint(item, idx, "pendentes");

          return (
            <React.Fragment key={`${item.label}-${idx}`}>
              <circle className="dot-resolvidos" cx={r.x} cy={r.y} r="4" />
              <circle className="dot-pendentes" cx={p.x} cy={p.y} r="4" />
            </React.Fragment>
          );
        })}
      </svg>

      <div className="dash-trend-labels">
        {items.map((item, idx) => (
          <span key={`${item.label}-${idx}`}>{item.label}</span>
        ))}
      </div>

      <div className="dash-trend-legend">
        <span>
          <i className="ok" /> Resolvidos
        </span>
        <span>
          <i className="bad" /> Pendentes
        </span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSetores, setLoadingSetores] = useState(false);
  const [erroDashboard, setErroDashboard] = useState("");
  const [showLayoutConfig, setShowLayoutConfig] = useState(false);

  const [filtros, setFiltros] = useState({
    setor: "SUPORTE",
    periodo: "MES_ATUAL",
    dataInicio: "",
    dataFim: "",
    includeMapa: true,
  });

  const layoutStorageKey = getUserLayoutKey();

  const [sectionOrder, setSectionOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(layoutStorageKey);
      if (!raw) return DASH_SECTION_IDS;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return DASH_SECTION_IDS;

      const valid = parsed.filter((id) => DASH_SECTION_IDS.includes(id));
      const missing = DASH_SECTION_IDS.filter((id) => !valid.includes(id));

      return [...valid, ...missing];
    } catch {
      return DASH_SECTION_IDS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(layoutStorageKey, JSON.stringify(sectionOrder));
    } catch {}
  }, [layoutStorageKey, sectionOrder]);

  useEffect(() => {
    let alive = true;

    setLoadingSetores(true);

    api
      .get("/setores", { params: { page: 1, limit: 100 } })
      .then((res) => {
        if (!alive) return;

        const payload = res.data || {};
        setSetores(Array.isArray(payload.data) ? payload.data : []);
      })
      .catch(() => {
        if (!alive) return;
        setSetores([]);
      })
      .finally(() => {
        if (alive) setLoadingSetores(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const loadDashboard = ({ refresh = false } = {}) => {
    setLoading(true);
    setErroDashboard("");

    const params = {
      ...getPeriodoParams(filtros),
      uf: "SP",
      mapLimit: 8000,
      includeMapa: filtros.includeMapa ? 1 : 0,
    };

    if (refresh) params.refresh = 1;

    if (filtros.setor && filtros.setor !== "TODOS") {
      params.setor = filtros.setor;
    }

    if (filtros.setor === "TODOS") {
      params.setor = "";
    }

    api
      .get("/dashboard", { params })
      .then((res) => {
        const payload = res.data || {};

        if (payload.success === false) {
          throw new Error(payload.message || "Falha ao carregar dashboard.");
        }

        setData(payload);
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Erro ao carregar dashboard.";

        setErroDashboard(message);
        showError(message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalized = useMemo(() => {
    const payload = data || {};
    const meta = payload.meta || {};
    const filtrosMeta = meta.filtros || {};

    const clientes = payload.clientes || {};
    const chamados = payload.chamados || {};
    const chamadosTotais = chamados.totais || {};

    const statusPeriodo =
      chamados.porStatusPeriodo ||
      chamados.porStatusMes ||
      chamados.statusPeriodo ||
      payload.statusPeriodo ||
      {
        emFila: 0,
        emAtendimento: 0,
        finalizados: 0,
      };

    const timelineRaw = asArray(
      chamados.porPeriodoTimeline,
      chamados.timelinePeriodo,
      chamados.periodoTimeline,
      chamados.timeline,
      chamados.porPeriodo,
      payload.chamadosPorPeriodo,
      payload.porPeriodoTimeline
    );

    const timeline = timelineRaw.map((item) => {
      const resolvidos = numberOrZero(
        pick(item, ["resolvidos", "Resolvidos", "RESOLVIDOS"])
      );
      const pendentes = numberOrZero(
        pick(item, ["pendentes", "Pendentes", "PENDENTES"])
      );
      const total =
        numberOrZero(pick(item, ["total", "Total", "TOTAL"])) ||
        resolvidos + pendentes;

      return {
        bucket: pick(item, ["bucket", "Bucket", "BUCKET", "data", "Data", "DATA"]),
        label:
          pick(item, ["label", "Label", "LABEL"]) ||
          formatDatePt(pick(item, ["bucket", "Bucket", "BUCKET", "data", "Data", "DATA"])) ||
          "Período",
        resolvidos,
        pendentes,
        total,
      };
    });

    const porPeriodoDoDia = asArray(
      chamados.porPeriodoDoDia,
      chamados.porPeriodoDiaMes,
      chamados.periodoDia,
      chamados.periodoDoDia,
      payload.chamadosPorPeriodoDoDia
    ).map((item) => ({
      periodo: textOrDefault(
        pick(item, ["periodo", "Periodo", "PERIODO", "label", "Label", "LABEL"])
      ),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const porTecnico = asArray(
      chamados.porTecnicoPeriodo,
      chamados.porTecnicoMes,
      chamados.porTecnico,
      payload.chamadosPorTecnico
    ).map((item) => ({
      tecnico: textOrDefault(
        pick(item, ["tecnico", "Tecnico", "TECNICO", "nome", "Nome", "NOME"])
      ),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const porUrgencia = asArray(
      chamados.porUrgenciaPeriodo,
      chamados.porUrgenciaMes,
      chamados.porUrgencia
    ).map((item) => {
      const urgencia = pick(item, ["urgencia", "Urgencia", "URGENCIA"]);
      return {
        urgencia,
        label:
          URGENCIA_LABEL[urgencia] ||
          textOrDefault(pick(item, ["label", "Label", "LABEL", "urgencia", "Urgencia", "URGENCIA"])),
        total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
      };
    });

    const clientesTop = asArray(
      clientes.topDemandantesPeriodo,
      clientes.topDemandantesMes,
      clientes.topClientes,
      payload.clientesTop
    ).map((item) => ({
      nome: textOrDefault(pick(item, ["nome", "Nome", "NOME", "cliente", "Cliente", "CLIENTE"])),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const problemasRecorrentes = asArray(
      chamados.problemasRecorrentesPeriodo,
      chamados.problemasRecorrentesAno,
      chamados.problemasRecorrentes,
      payload.problemasRecorrentes
    ).map((item) => ({
      problema: textOrDefault(
        pick(item, ["problema", "Problema", "PROBLEMA", "label", "Label", "LABEL"])
      ),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const backlogPorSetor = asArray(
      chamados.backlogPorSetor,
      chamados.backlogPorSetorAno
    ).map((item) => {
      const abertos = numberOrZero(pick(item, ["abertos", "Abertos", "ABERTOS"]));
      return {
        setor: textOrDefault(pick(item, ["setor", "Setor", "SETOR"])),
        abertos,
        total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])) || abertos,
      };
    });

    const problemasPorSubproblema = asArray(
      chamados.problemasPorSubproblemaPeriodo,
      chamados.problemasPorSubproblemaMes,
      chamados.problemasPorSubproblema
    ).map((item) => {
      const problema = textOrDefault(pick(item, ["problema", "Problema", "PROBLEMA"]));
      const subproblema = textOrDefault(
        pick(item, ["subproblema", "Subproblema", "SUBPROBLEMA"])
      );

      return {
        problema,
        subproblema,
        label: `${problema} • ${subproblema}`,
        total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
      };
    });

    const projetos = payload.projetos || {};
    const projetosTotais = projetos.totais || {};

    const projetosStatus = asArray(projetos.porStatus, projetos.status).map((item) => ({
      status: textOrDefault(pick(item, ["status", "Status", "STATUS"])),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const projetosPrioridade = asArray(
      projetos.porPrioridadeAtivos,
      projetos.porPrioridade,
      projetos.prioridade
    ).map((item) => ({
      prioridade: textOrDefault(pick(item, ["prioridade", "Prioridade", "PRIORIDADE"])),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const projetosAtrasados = asArray(projetos.atrasadosTop10, projetos.atrasados).map(
      (item) => ({
        id: pick(item, ["idProjeto", "id", "ID_PROJETO", "codigo", "CODIGO"]),
        codigo: pick(item, ["codigo", "CODIGO"]) || "-",
        titulo: textOrDefault(
          pick(item, ["titulo", "TITULO", "Titulo"]),
          "Projeto sem título"
        ),
        status: textOrDefault(pick(item, ["status", "STATUS", "Status"])),
        prioridade: textOrDefault(pick(item, ["prioridade", "PRIORIDADE", "Prioridade"])),
        diasAtraso: numberOrZero(
          pick(item, ["diasAtraso", "DIAS_ATRASO", "DiasAtraso"])
        ),
      })
    );

    const projetosPorUsuario = asArray(
      projetos.porUsuarioAtivos,
      projetos.porDevAtivos,
      projetos.porUsuario
    ).map((item) => ({
      nomeUsuario:
        pick(item, ["nomeDev", "usuario", "devNome", "nomeUsuario", "NOME_USUARIO"]) ||
        `ID ${pick(item, ["devId", "ID_DEV"], "-")}`,
      totalProjetos: numberOrZero(
        pick(item, ["totalProjetos", "total", "TOTAL", "TOTAL_PROJETOS"])
      ),
    }));

    const tarefas = payload.tarefas || {};
    const tarefasTotais = tarefas.totais || {};

    const leadsResumo = payload.leads?.resumo || {};

    const leadsPorStatus = [
      { status: "NOVO", label: "Novo", total: numberOrZero(leadsResumo.totalNovo) },
      {
        status: "QUALIFICANDO",
        label: "Qualificando",
        total: numberOrZero(leadsResumo.totalQualificando),
      },
      {
        status: "PROPOSTA",
        label: "Proposta",
        total: numberOrZero(leadsResumo.totalProposta),
      },
      { status: "GANHO", label: "Ganho", total: numberOrZero(leadsResumo.totalGanho) },
      {
        status: "PERDIDO",
        label: "Perdido",
        total: numberOrZero(leadsResumo.totalPerdido),
      },
    ];

    const leadsPorCanal = asArray(payload.leads?.porCanal).map((item) => ({
      canal: textOrDefault(pick(item, ["CANAL", "canal", "Canal"])),
      total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
    }));

    const leadsPorOwner = asArray(payload.leads?.porOwner).map((item) => {
      const ownerId = pick(item, ["OWNER_ID", "ownerId", "OwnerId"], null);
      return {
        ownerId,
        label: ownerId == null ? "Sem responsável" : `Owner ${ownerId}`,
        total: numberOrZero(pick(item, ["total", "Total", "TOTAL"])),
      };
    });

    const mapaClientes = payload.mapaClientes || {};
    const mapaPontos = asArray(mapaClientes.pontos)
      .map((p) => ({
        lat: Number(p.lat),
        lng: Number(p.lng),
        nome: p.nome,
        meta: {
          codCliente: p.codCliente,
          cidade: p.cidade,
          bairro: p.bairro,
          precisao: p.precisao,
          fonte: p.fonte,
        },
      }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));

    return {
      meta,
      filtrosMeta,
      clientes: {
        totalAtivos: numberOrZero(
          clientes.totalAtivos ?? clientes.TOTAL_ATIVOS ?? payload.totalClientes
        ),
        totalComChamadosPeriodo: numberOrZero(
          clientes.totalComChamadosPeriodo ??
            clientes.totalComChamadosAno ??
            clientes.TOTAL_COM_CHAMADOS_PERIODO
        ),
        topDemandantesPeriodo: clientesTop,
      },
      chamados: {
        totais: {
          ano: numberOrZero(
            chamadosTotais.ano ?? chamadosTotais.ANO ?? payload.totalChamados
          ),
          periodo: numberOrZero(
            chamadosTotais.periodo ??
              chamadosTotais.mes ??
              chamadosTotais.PERIODO ??
              payload.chamadosMes
          ),
          hoje: numberOrZero(chamadosTotais.hoje ?? chamadosTotais.HOJE),
        },
        porStatusPeriodo: {
          emFila: numberOrZero(statusPeriodo.emFila ?? statusPeriodo.EM_FILA),
          emAtendimento: numberOrZero(
            statusPeriodo.emAtendimento ?? statusPeriodo.EM_ATENDIMENTO
          ),
          finalizados: numberOrZero(statusPeriodo.finalizados ?? statusPeriodo.FINALIZADOS),
        },
        porPeriodoTimeline: timeline,
        porPeriodoDoDia,
        porTecnico,
        porUrgencia,
        backlogPorSetor,
        problemasRecorrentes,
        problemasPorSubproblema,
      },
      projetos: {
        totais: {
          ativos: numberOrZero(projetosTotais.ativos ?? projetosTotais.ATIVOS),
          concluidosAno: numberOrZero(
            projetosTotais.concluidosAno ?? projetosTotais.CONCLUIDOS_ANO
          ),
          atrasados: numberOrZero(projetosTotais.atrasados ?? projetosTotais.ATRASADOS),
        },
        porStatus: projetosStatus,
        porPrioridadeAtivos: projetosPrioridade,
        atrasadosTop10: projetosAtrasados,
        porUsuarioAtivos: projetosPorUsuario,
      },
      tarefas: {
        totais: {
          abertas: numberOrZero(tarefasTotais.abertas ?? tarefasTotais.ABERTAS),
          concluidasMes: numberOrZero(
            tarefasTotais.concluidasMes ?? tarefasTotais.CONCLUIDAS_MES
          ),
        },
      },
      leads: {
        resumo: {
          totalLeads: numberOrZero(leadsResumo.totalLeads),
          totalNovo: numberOrZero(leadsResumo.totalNovo),
          totalQualificando: numberOrZero(leadsResumo.totalQualificando),
          totalProposta: numberOrZero(leadsResumo.totalProposta),
          totalGanho: numberOrZero(leadsResumo.totalGanho),
          totalPerdido: numberOrZero(leadsResumo.totalPerdido),
          semOwner: numberOrZero(leadsResumo.semOwner),
        },
        porStatus: leadsPorStatus,
        porCanal: leadsPorCanal,
        porOwner: leadsPorOwner,
      },
      mapaClientes: {
        uf: String(mapaClientes.uf || "SP").toUpperCase(),
        total: numberOrZero(mapaClientes.total ?? mapaPontos.length),
        included: mapaClientes.included !== false,
        pontos: mapaPontos,
      },
    };
  }, [data]);

  if (loading && !data) {
    return (
      <MainLayout>
        <div className="dashboard-container">
          <div className="dashboard-header-skeleton">
            <Skeleton height={36} width={280} style={{ marginBottom: 10 }} />
            <Skeleton height={30} width={180} />
          </div>
          <Skeleton height={96} count={1} style={{ marginBottom: 20 }} />
          <Skeleton height={190} count={4} style={{ marginBottom: 20 }} />
        </div>
      </MainLayout>
    );
  }

  const { meta, filtrosMeta } = normalized;

  let periodoLabel = filtrosMeta.periodoLabel || "";

  if (!periodoLabel) {
    if (meta.ano && meta.mes) {
      const nomeMes = new Date(meta.ano, meta.mes - 1, 1).toLocaleString("pt-BR", {
        month: "long",
      });

      periodoLabel = `${nomeMes.charAt(0).toUpperCase()}${nomeMes.slice(1)} / ${
        meta.ano
      }`;
    } else if (meta.ano) {
      periodoLabel = `Ano ${meta.ano}`;
    } else {
      periodoLabel = "Período atual";
    }
  }

  const dataInicioLabel = formatDatePt(filtrosMeta.dataInicio);
  const dataFimLabel = formatDatePt(filtrosMeta.dataFim);

  const statusPeriodo = normalized.chamados.porStatusPeriodo;
  const totalStatusPeriodo =
    statusPeriodo.emFila + statusPeriodo.emAtendimento + statusPeriodo.finalizados;

  const taxaFinalizacaoPeriodo = totalStatusPeriodo
    ? Math.round((statusPeriodo.finalizados / totalStatusPeriodo) * 100)
    : 0;

  const leadsProspeccao =
    normalized.leads.resumo.totalNovo + normalized.leads.resumo.totalQualificando;

  const mapaPontos = normalized.mapaClientes.pontos;
  const mapaTotalPontos = normalized.mapaClientes.total || mapaPontos.length;

  const moveSection = (id, delta) => {
    setSectionOrder((curr) => {
      const idx = curr.indexOf(id);
      if (idx === -1) return curr;

      const newIdx = idx + delta;
      if (newIdx < 0 || newIdx >= curr.length) return curr;

      const next = [...curr];
      const [item] = next.splice(idx, 1);
      next.splice(newIdx, 0, item);

      return next;
    });
  };

  const hideSection = (id) => {
    setSectionOrder((curr) => curr.filter((item) => item !== id));
  };

  const showSection = (id) => {
    setSectionOrder((curr) => (curr.includes(id) ? curr : [...curr, id]));
  };

  const resetLayout = () => {
    setSectionOrder(DASH_SECTION_IDS);

    try {
      localStorage.removeItem(layoutStorageKey);
    } catch {}
  };

  const sectionDefs = [
    {
      id: "indicadores-principais",
      title: SECTION_TITLES["indicadores-principais"],
      render: () => (
        <SectionShell>
          <div className="indicadores-grid indicadores-principais">
            <Card
              title="Clientes Ativos"
              value={normalized.clientes.totalAtivos}
              icon={<Users size={24} color="#fff" />}
              color="#22c55e"
              description="Clientes de software com contrato ativo"
            />
            <Card
              title="Clientes com Chamados no Período"
              value={normalized.clientes.totalComChamadosPeriodo}
              icon={<Activity size={24} color="#fff" />}
              color="#60a5fa"
              description="Base engajada no período selecionado"
            />
            <Card
              title="Chamados no Ano"
              value={normalized.chamados.totais.ano}
              icon={<ClipboardList size={24} color="#fff" />}
              color="#38bdf8"
              description="Total de chamados na visão anual"
            />
            <Card
              title="Chamados no Período"
              value={normalized.chamados.totais.periodo}
              icon={<CalendarCheck size={24} color="#fff" />}
              color="#ef4444"
              description="Chamados registrados no período selecionado"
            />
          </div>
        </SectionShell>
      ),
    },
    {
      id: "indicadores-secundarios",
      title: SECTION_TITLES["indicadores-secundarios"],
      render: () => (
        <SectionShell>
          <div className="indicadores-grid indicadores-secundarios">
            <Card
              title="Chamados Hoje"
              value={normalized.chamados.totais.hoje}
              icon={<Clock size={22} color="#fff" />}
              color="#f59e0b"
              description="Fluxo do dia corrente"
            />
            <Card
              title="Em Fila (Período)"
              value={statusPeriodo.emFila}
              icon={<ClipboardList size={22} color="#fff" />}
              color="#64748b"
              description="Aguardando atendimento no período"
            />
            <Card
              title="Em Atendimento (Período)"
              value={statusPeriodo.emAtendimento}
              icon={<Activity size={22} color="#fff" />}
              color="#2563eb"
              description="Atendimentos ativos no período"
            />
            <Card
              title="Finalizados (Período)"
              value={statusPeriodo.finalizados}
              icon={<CheckCircle size={22} color="#fff" />}
              color="#22c55e"
              description="Chamados concluídos no período"
            />
          </div>
        </SectionShell>
      ),
    },
    {
      id: "mapa-clientes-sp",
      title: SECTION_TITLES["mapa-clientes-sp"],
      render: () => (
        <SectionShell
          title="Distribuição geográfica"
          subtitle={`Clientes de software ativos com geocodificação válida no estado de ${normalized.mapaClientes.uf}`}
          right={
            <div className="section-header-kpis">
              <div className="mapa-kpi">
                <span>Clientes ativos</span>
                <strong>{formatNumber(normalized.clientes.totalAtivos)}</strong>
              </div>
              <div className="mapa-kpi">
                <span>Clientes no mapa</span>
                <strong>{formatNumber(mapaTotalPontos)}</strong>
              </div>
            </div>
          }
        >
          <div className="section-mapa-clientes">
            {!normalized.mapaClientes.included && (
              <div className="mapa-empty">
                Mapa não carregado nesta consulta. Ative o mapa nos filtros e atualize.
              </div>
            )}

            {normalized.mapaClientes.included && mapaPontos.length === 0 && (
              <div className="mapa-empty">
                Nenhum cliente geocodificado para o filtro atual.
              </div>
            )}

            {normalized.mapaClientes.included && (
              <MapaClientesSP pontos={mapaPontos} height={420} />
            )}
          </div>
        </SectionShell>
      ),
    },
    {
      id: "leads-metricas",
      title: SECTION_TITLES["leads-metricas"],
      render: () => (
        <SectionShell>
          <div className="indicadores-grid indicadores-leads">
            <Card
              title="Leads no Período"
              value={normalized.leads.resumo.totalLeads}
              icon={<Users size={22} color="#fff" />}
              color="#a855f7"
              description="Leads criados no período selecionado"
            />
            <Card
              title="Em Prospecção"
              value={leadsProspeccao}
              icon={<Activity size={22} color="#fff" />}
              color="#14b8a6"
              description="Novos + em qualificação"
            />
            <Card
              title="Em Proposta"
              value={normalized.leads.resumo.totalProposta}
              icon={<ClipboardList size={22} color="#fff" />}
              color="#38bdf8"
              description="Leads com proposta enviada"
            />
            <Card
              title="Leads Ganhos"
              value={normalized.leads.resumo.totalGanho}
              icon={<CheckCircle size={22} color="#fff" />}
              color="#22c55e"
              description="Oportunidades convertidas em clientes"
            />
          </div>

          <div className="graficos-grid">
            <ChartCard
              title="Leads por Status"
              subtitle="Distribuição dos leads no funil comercial"
              empty={!hasChartData(normalized.leads.porStatus)}
              emptyTitle="Sem leads no período"
              emptyDescription="Quando houver leads criados, o funil será exibido aqui."
            >
              <BarVisual data={normalized.leads.porStatus} labelKey="label" valueKey="total" />
            </ChartCard>

            <ChartCard
              title="Leads por Canal"
              subtitle="Origem dos leads captados no período"
              empty={!hasChartData(normalized.leads.porCanal)}
              emptyTitle="Sem origem de leads"
              emptyDescription="Os canais aparecerão quando houver leads no período."
            >
              <DonutVisual data={normalized.leads.porCanal} labelKey="canal" valueKey="total" />
            </ChartCard>
          </div>

          {normalized.leads.porOwner.length > 0 && (
            <div className="graficos-grid leads-owner-row">
              <ChartCard
                title="Leads por Responsável"
                subtitle={`Distribuição da carteira de leads entre usuários${
                  normalized.leads.resumo.semOwner
                    ? ` • ${normalized.leads.resumo.semOwner} sem responsável`
                    : ""
                }`}
                className="grafico-tecnico"
                empty={!hasChartData(normalized.leads.porOwner)}
              >
                <BarVisual
                  data={normalized.leads.porOwner}
                  labelKey="label"
                  valueKey="total"
                  mode="horizontal"
                />
              </ChartCard>
            </div>
          )}
        </SectionShell>
      ),
    },
    {
      id: "chamados-mensal-periodo",
      title: SECTION_TITLES["chamados-mensal-periodo"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Chamados no Período"
              subtitle="Evolução de chamados resolvidos e pendentes"
              className="grafico-large"
              empty={!hasChartData(normalized.chamados.porPeriodoTimeline, "total")}
              emptyTitle="Sem chamados no período"
              emptyDescription="Altere o filtro ou aguarde novos chamados para visualizar a evolução."
              meta={
                totalStatusPeriodo > 0 && (
                  <span>
                    Taxa de finalização: <strong>{taxaFinalizacaoPeriodo}%</strong>
                  </span>
                )
              }
            >
              <TrendVisual data={normalized.chamados.porPeriodoTimeline} />
            </ChartCard>

            <ChartCard
              title="Chamados por Período do Dia"
              subtitle="Distribuição ao longo do dia no período selecionado"
              empty={!hasChartData(normalized.chamados.porPeriodoDoDia)}
              emptyTitle="Sem chamados por horário"
              emptyDescription="O gráfico será preenchido quando houver chamados no período."
            >
              <DonutVisual
                data={normalized.chamados.porPeriodoDoDia}
                labelKey="periodo"
                valueKey="total"
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
    {
      id: "chamados-tecnico-urgencia",
      title: SECTION_TITLES["chamados-tecnico-urgencia"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Chamados por Técnico"
              subtitle="Carga de chamados atribuídos a cada técnico no período"
              className="grafico-tecnico"
              empty={!hasChartData(normalized.chamados.porTecnico)}
              emptyTitle="Sem chamados por técnico"
              emptyDescription="Nenhum técnico teve chamados no período selecionado."
            >
              <BarVisual
                data={normalized.chamados.porTecnico}
                labelKey="tecnico"
                valueKey="total"
                mode="horizontal"
              />
            </ChartCard>

            <ChartCard
              title="Chamados por Urgência"
              subtitle="Nível de criticidade das demandas de suporte"
              empty={!hasChartData(normalized.chamados.porUrgencia)}
              emptyTitle="Sem urgência no período"
              emptyDescription="Os níveis de urgência serão exibidos quando houver chamados."
            >
              <DonutVisual
                data={normalized.chamados.porUrgencia}
                labelKey="label"
                valueKey="total"
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
    {
      id: "clientes-problemas",
      title: SECTION_TITLES["clientes-problemas"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Top 5 Clientes com Mais Chamados"
              subtitle="Clientes com maior volume de chamados no período"
              empty={!hasChartData(normalized.clientes.topDemandantesPeriodo)}
              emptyTitle="Sem ranking de clientes"
              emptyDescription="Nenhum cliente gerou chamados no período selecionado."
            >
              <DonutVisual
                data={normalized.clientes.topDemandantesPeriodo}
                labelKey="nome"
                valueKey="total"
              />
            </ChartCard>

            <ChartCard
              title="Problemas Mais Recorrentes"
              subtitle="Principais tipos de problemas reportados no período"
              empty={!hasChartData(normalized.chamados.problemasRecorrentes)}
              emptyTitle="Sem problemas recorrentes"
              emptyDescription="Nenhuma categoria de problema foi encontrada no período."
            >
              <BarVisual
                data={normalized.chamados.problemasRecorrentes}
                labelKey="problema"
                valueKey="total"
                mode="horizontal"
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
    {
      id: "backlog-subproblemas",
      title: SECTION_TITLES["backlog-subproblemas"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Backlog de Chamados por Setor"
              subtitle="Quantidade de chamados em aberto por setor"
              empty={!hasChartData(normalized.chamados.backlogPorSetor, "abertos")}
              emptyTitle="Sem backlog aberto"
              emptyDescription="Não há chamados abertos para compor backlog."
            >
              <BarVisual
                data={normalized.chamados.backlogPorSetor}
                labelKey="setor"
                valueKey="abertos"
              />
            </ChartCard>

            <ChartCard
              title="Problemas x Subproblemas"
              subtitle="Visão afunilada das causas mais recorrentes"
              empty={!hasChartData(normalized.chamados.problemasPorSubproblema)}
              emptyTitle="Sem subproblemas"
              emptyDescription="Nenhum subproblema foi registrado no período."
            >
              <BarVisual
                data={normalized.chamados.problemasPorSubproblema}
                labelKey="label"
                valueKey="total"
                mode="horizontal"
                maxItems={8}
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
    {
      id: "delivery-kpis",
      title: SECTION_TITLES["delivery-kpis"],
      render: () => (
        <SectionShell
          title="Delivery de Projetos & Tarefas"
          subtitle="Acompanhamento da esteira de desenvolvimento e solicitações internas"
        >
          <div className="indicadores-grid">
            <Card
              title="Projetos Ativos"
              value={normalized.projetos.totais.ativos}
              icon={<Layers size={22} color="#fff" />}
              color="#38bdf8"
              description="Projetos em andamento"
            />
            <Card
              title="Projetos Concluídos no Ano"
              value={normalized.projetos.totais.concluidosAno}
              icon={<CheckCircle size={22} color="#fff" />}
              color="#22c55e"
              description="Entregas realizadas em produção"
            />
            <Card
              title="Projetos Atrasados"
              value={normalized.projetos.totais.atrasados}
              icon={<AlertTriangle size={22} color="#fff" />}
              color="#ef4444"
              description="Projetos com prazo estourado"
            />
            <Card
              title="Tarefas em Andamento"
              value={normalized.tarefas.totais.abertas}
              icon={<ListChecks size={22} color="#fff" />}
              color="#f59e0b"
              description="Itens em andamento na esteira"
            />
          </div>
        </SectionShell>
      ),
    },
    {
      id: "projetos-status-prioridade",
      title: SECTION_TITLES["projetos-status-prioridade"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Status dos Projetos"
              subtitle="Distribuição dos projetos por status operacional"
              empty={!hasChartData(normalized.projetos.porStatus)}
              emptyTitle="Sem projetos por status"
              emptyDescription="Nenhum projeto localizado para compor este gráfico."
            >
              <DonutVisual
                data={normalized.projetos.porStatus}
                labelKey="status"
                valueKey="total"
              />
            </ChartCard>

            <ChartCard
              title="Projetos por Prioridade"
              subtitle="Foco da equipe por nível de prioridade"
              empty={!hasChartData(normalized.projetos.porPrioridadeAtivos)}
              emptyTitle="Sem prioridade de projetos"
              emptyDescription="Nenhum projeto ativo possui prioridade para este período."
            >
              <BarVisual
                data={normalized.projetos.porPrioridadeAtivos}
                labelKey="prioridade"
                valueKey="total"
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
    {
      id: "projetos-atrasados-dev",
      title: SECTION_TITLES["projetos-atrasados-dev"],
      render: () => (
        <SectionShell>
          <div className="graficos-grid">
            <ChartCard
              title="Projetos Atrasados"
              subtitle="Top projetos com maior número de dias em atraso"
              className="grafico-lista"
            >
              <div className="lista-projetos-atrasados">
                {normalized.projetos.atrasadosTop10.length === 0 && (
                  <p className="lista-empty">Nenhum projeto atrasado no momento.</p>
                )}

                {normalized.projetos.atrasadosTop10.map((item) => (
                  <div key={item.id} className="projeto-atrasado-item">
                    <div className="projeto-atrasado-main">
                      <span className="projeto-codigo">{item.codigo}</span>
                      <span className="projeto-titulo">{item.titulo}</span>
                    </div>
                    <div className="projeto-atrasado-meta">
                      <span className="chip-critico">{item.diasAtraso} dia(s) em atraso</span>
                    </div>
                  </div>
                ))}
              </div>
            </ChartCard>

            <ChartCard
              title="Carga de Projetos por Usuário"
              subtitle="Distribuição da carteira de projetos ativos por usuário"
              empty={!hasChartData(normalized.projetos.porUsuarioAtivos, "totalProjetos")}
              emptyTitle="Sem carga por usuário"
              emptyDescription="Nenhuma carteira ativa para exibir."
            >
              <BarVisual
                data={normalized.projetos.porUsuarioAtivos}
                labelKey="nomeUsuario"
                valueKey="totalProjetos"
                mode="horizontal"
              />
            </ChartCard>
          </div>
        </SectionShell>
      ),
    },
  ];

  const visibleSections = sectionOrder
    .map((id) => sectionDefs.find((sec) => sec.id === id))
    .filter(Boolean);

  const hiddenSections = sectionDefs.filter((sec) => !sectionOrder.includes(sec.id));

  return (
    <MainLayout>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Visão Executiva</h1>
            <p className="dashboard-subtitle">
              Performance do suporte, projetos, demanda de clientes e leads •{" "}
              <strong>{periodoLabel}</strong>
              {dataInicioLabel && dataFimLabel && (
                <>
                  {" "}
                  ({dataInicioLabel} a {dataFimLabel})
                </>
              )}
            </p>
          </div>

          <div className="dashboard-header-actions">
            <span className={`dashboard-badge ${meta.fromCache ? "is-cache" : ""}`}>
              <span className="badge-dot" />
              {meta.fromCache ? "Dados em cache" : "Dados atualizados"}
            </span>
            <span className="dashboard-generated">
              Atualizado em {formatDateTimePt(meta.generatedAt)}
            </span>
          </div>
        </div>

        {erroDashboard && (
          <div className="dashboard-error">
            <AlertTriangle size={18} />
            <span>{erroDashboard}</span>
          </div>
        )}

        <div className="dashboard-filtros">
          <div className="filtro-item">
            <label>Setor</label>
            <select
              value={filtros.setor}
              disabled={loadingSetores || loading}
              onChange={(event) =>
                setFiltros((prev) => ({ ...prev, setor: event.target.value }))
              }
            >
              <option value="SUPORTE">Suporte</option>
              <option value="TODOS">Todos os setores</option>
              {setores.map((item) => (
                <option key={item.ID || item.Setor} value={item.Setor}>
                  {item.Setor}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-item">
            <label>Período</label>
            <select
              value={filtros.periodo}
              disabled={loading}
              onChange={(event) =>
                setFiltros((prev) => ({ ...prev, periodo: event.target.value }))
              }
            >
              <option value="MES_ATUAL">Mês atual</option>
              <option value="ANO_ATUAL">Ano atual</option>
              <option value="INTERVALO">Período customizado</option>
            </select>
          </div>

          {filtros.periodo === "INTERVALO" && (
            <div className="filtro-item filtro-data-range">
              <label>De</label>
              <input
                type="date"
                value={filtros.dataInicio}
                disabled={loading}
                onChange={(event) =>
                  setFiltros((prev) => ({ ...prev, dataInicio: event.target.value }))
                }
              />

              <label>Até</label>
              <input
                type="date"
                value={filtros.dataFim}
                disabled={loading}
                onChange={(event) =>
                  setFiltros((prev) => ({ ...prev, dataFim: event.target.value }))
                }
              />
            </div>
          )}

          <div className="filtro-item resumo-periodo">
            <span>
              Período: <strong>{periodoLabel}</strong>
            </span>
            {dataInicioLabel && dataFimLabel && (
              <span>
                Janela:{" "}
                <strong>
                  {dataInicioLabel} a {dataFimLabel}
                </strong>
              </span>
            )}
            <span>
              Mapa: <strong>{filtros.includeMapa ? "Ativo" : "Desativado"}</strong>
            </span>
          </div>

          <div className="filtro-item filtro-actions">
            <button
              type="button"
              className="btn-ghost"
              disabled={loading}
              onClick={() =>
                setFiltros((prev) => ({ ...prev, includeMapa: !prev.includeMapa }))
              }
            >
              <MapPinned size={16} />
              {filtros.includeMapa ? "Sem mapa" : "Com mapa"}
            </button>

            <button
              type="button"
              className="btn-ghost"
              disabled={loading}
              onClick={() => loadDashboard({ refresh: true })}
            >
              <RefreshCcw size={16} />
              Filtrar
            </button>

            <button
              type="button"
              className={`btn-ghost ${showLayoutConfig ? "btn-ghost-active" : ""}`}
              onClick={() => setShowLayoutConfig((prev) => !prev)}
            >
              <SlidersHorizontal size={16} />
              Personalizar
            </button>
          </div>
        </div>

        {showLayoutConfig && (
          <div className="dashboard-layout-config">
            <div className="layout-config-header">
              <strong>Layout do painel</strong>
              <span className="muted">
                Reordene ou oculte blocos. As preferências são salvas por usuário.
              </span>
            </div>

            <div className="layout-config-body">
              {visibleSections.map((sec, idx) => (
                <div key={sec.id} className="layout-config-row">
                  <span className="layout-config-title">{sec.title}</span>

                  <div className="layout-config-actions">
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={idx === 0}
                      onClick={() => moveSection(sec.id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={idx === visibleSections.length - 1}
                      onClick={() => moveSection(sec.id, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => hideSection(sec.id)}
                    >
                      Ocultar
                    </button>
                  </div>
                </div>
              ))}

              {hiddenSections.length > 0 && (
                <div className="layout-config-hidden">
                  <span className="muted">Blocos ocultos:</span>

                  <div className="layout-config-hidden-list">
                    {hiddenSections.map((sec) => (
                      <button
                        type="button"
                        key={sec.id}
                        className="btn-ghost"
                        onClick={() => showSection(sec.id)}
                      >
                        + {sec.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="layout-config-footer">
                <button type="button" className="btn-ghost" onClick={resetLayout}>
                  Restaurar layout padrão
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && data && (
          <div className="dashboard-refreshing">
            <RefreshCcw size={16} />
            Atualizando indicadores...
          </div>
        )}

        {visibleSections.map((sec) => (
          <React.Fragment key={sec.id}>{sec.render()}</React.Fragment>
        ))}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
